import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Save, Loader2, RotateCcw, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SCENARIO_META = [
  { key: 'pacto',        label: 'Pacto / Quita',          fields: ['gastos_legales','itp_pct','notaria_registro_pct','capex_pct','meses'] },
  { key: 'cobertura',    label: 'Subasta / Cobertura',    fields: ['gastos_legales','ajd_pct','notaria_registro_pct','deuda_total_multiplicador','meses'] },
  { key: 'cdr',          label: 'Subasta / CDR',          fields: ['gastos_legales','ajd_pct','notaria_registro_pct','meses'] },
  { key: 'adjudicacion', label: 'Subasta / Adjudicación', fields: ['gastos_legales','ajd_pct','itp_pct','notaria_registro_pct','capex_pct','meses'] },
];

const FIELD_META = {
  gastos_legales:            { label: 'Gastos legales (€)',                       unit: '€',   step: 50 },
  itp_pct:                   { label: 'ITP (% sobre base)',                       unit: '%',   step: 0.5,  pct: true },
  ajd_pct:                   { label: 'AJD (% sobre precio compra)',              unit: '%',   step: 0.1,  pct: true },
  notaria_registro_pct:      { label: 'Notaría + registro (% sobre precio)',       unit: '%',   step: 0.1,  pct: true },
  capex_pct:                 { label: 'CAPEX / Reforma (% sobre valor salida)',   unit: '%',   step: 0.5,  pct: true },
  deuda_total_multiplicador: { label: 'Multiplicador deuda total (para ingreso)', unit: 'x',   step: 0.05, mul: true },
  meses:                     { label: 'Meses estimados',                          unit: 'm',   step: 1 },
};

const deepClone = (o) => JSON.parse(JSON.stringify(o));

export const CreditoScoringSettingsSection = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/credito-scoring`, { withCredentials: true })
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Error al cargar parámetros de scoring crédito'))
      .finally(() => setLoading(false));
  }, []);

  const set = (path, value) => {
    setSettings(prev => {
      const next = deepClone(prev);
      const parts = path.split('.');
      let o = next;
      for (let i = 0; i < parts.length - 1; i++) o = o[parts[i]];
      o[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/credito-scoring`, settings, { withCredentials: true });
      toast.success('Parámetros guardados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar los valores por defecto del scoring crédito?')) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/settings/credito-scoring/reset`, {}, { withCredentials: true });
      setSettings(r.data.params);
      toast.success('Valores por defecto restaurados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restaurar');
    } finally { setLoading(false); }
  };

  const weightSum = useMemo(() => {
    if (!settings?.scoring?.weights) return 0;
    const w = settings.scoring.weights;
    return (w.roi || 0) + (w.roi_anualizado || 0) + (w.margen || 0);
  }, [settings]);

  const aggSum = useMemo(() => {
    if (!settings?.aggregation) return 0;
    const a = settings.aggregation;
    return (a.cobertura || 0) + (a.peor || 0) + (a.media_restantes || 0);
  }, [settings]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!settings) return <p className="text-slate-500 py-10">No se pudieron cargar los parámetros.</p>;

  return (
    <div className="space-y-6" data-testid="credito-scoring-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><Calculator className="w-5 h-5 text-blue-600" /> Parámetros de Scoring Crédito</h2>
          <p className="body-text mt-1">Todos los valores del motor de puntuación por escenario son configurables. Los cambios se aplican inmediatamente a cualquier ficha que pulse "Recalcular desde datos".</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleReset} className="btn-secondary text-sm flex items-center gap-2" data-testid="credito-settings-reset-btn">
            <RotateCcw className="w-4 h-4" /> Restaurar defecto
          </button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="credito-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      {/* ===== Scoring weights ===== */}
      <Section title="Fórmula del score por escenario"
               help={`score = w_roi × score(ROI) + w_roi_anual × score(ROI anualizado) + w_margen × score(margen). Los tres pesos deben sumar aprox 1.00.`}
               warn={Math.abs(weightSum - 1) > 0.001 ? `Peso total: ${weightSum.toFixed(2)} (se recomienda 1.00)` : null}>
        <div className="grid md:grid-cols-3 gap-3">
          <NumInput label="Peso ROI"            value={settings.scoring?.weights?.roi}             step={0.05} onChange={v => set('scoring.weights.roi', v)}            tid="w-roi" />
          <NumInput label="Peso ROI anualizado" value={settings.scoring?.weights?.roi_anualizado}  step={0.05} onChange={v => set('scoring.weights.roi_anualizado', v)} tid="w-roi-a" />
          <NumInput label="Peso margen"         value={settings.scoring?.weights?.margen}          step={0.05} onChange={v => set('scoring.weights.margen', v)}          tid="w-margen" />
        </div>
      </Section>

      {/* ===== Scoring scales ===== */}
      <Section title="Escalas de puntuación (saturación en 100)"
               help="Valor al que cada métrica alcanza 100 puntos. Por debajo de 0 siempre puntúa 0.">
        <div className="grid md:grid-cols-3 gap-3">
          <NumInput label="ROI ≥ (se puntúa 100)"            value={(settings.scoring?.scales?.roi_max_pct ?? 0) * 100}            step={5} suffix="%" onChange={v => set('scoring.scales.roi_max_pct', v / 100)}            tid="s-roi-max" />
          <NumInput label="ROI anualizado ≥ (se puntúa 100)" value={(settings.scoring?.scales?.roi_anualizado_max_pct ?? 0) * 100} step={5} suffix="%" onChange={v => set('scoring.scales.roi_anualizado_max_pct', v / 100)} tid="s-roi-a-max" />
          <NumInput label="Margen ≥ (se puntúa 100)"         value={settings.scoring?.scales?.margen_max_eur}                     step={5000} suffix="€" onChange={v => set('scoring.scales.margen_max_eur', v)}                tid="s-margen-max" />
        </div>
      </Section>

      {/* ===== Aggregation weights ===== */}
      <Section title="Pesos del score financiero TOTAL"
               help="score_total = w_cobertura × score_cobertura + w_peor × score_peor + w_media × media_restantes. Deben sumar aprox 1.00."
               warn={Math.abs(aggSum - 1) > 0.001 ? `Peso total: ${aggSum.toFixed(2)} (se recomienda 1.00)` : null}>
        <div className="grid md:grid-cols-3 gap-3">
          <NumInput label="Peso cobertura"          value={settings.aggregation?.cobertura}        step={0.05} onChange={v => set('aggregation.cobertura', v)}        tid="agg-cobertura" />
          <NumInput label="Peso peor escenario"     value={settings.aggregation?.peor}             step={0.05} onChange={v => set('aggregation.peor', v)}             tid="agg-peor" />
          <NumInput label="Peso media restantes"    value={settings.aggregation?.media_restantes}  step={0.05} onChange={v => set('aggregation.media_restantes', v)}  tid="agg-media" />
        </div>
      </Section>

      {/* ===== Impuestos por escenario (según reglas de impuestos) ===== */}
      <Section title="Impuestos aplicados por escenario"
               help="Las reglas se definen en la pestaña Parámetros › Impuestos (2 categorías: Adjudicación Inmueble y Compra de Crédito). El Scoring Crédito aplica automáticamente: PACTO → Adjudicación Inmueble · COBERTURA / CDR → Compra de Crédito · ADJUDICACIÓN → Compra de Crédito + Adjudicación Inmueble.">
        <ScoringTaxOverview />
      </Section>

      {/* ===== Per-scenario parameters ===== */}
      <Section title="Valores por escenario (autocompletado)"
               help="Estos son los valores que el botón 'Recalcular desde datos' aplica automáticamente. Cada escenario se nutre de los datos del activo (precio compra, valor referencia, deuda total, valor adjudicación, valor salida)."
      >
        <div className="grid lg:grid-cols-2 gap-4">
          {SCENARIO_META.map(sm => (
            <div key={sm.key} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50" data-testid={`sc-${sm.key}`}>
              <h3 className="font-semibold text-slate-800 mb-3 text-sm">{sm.label}</h3>
              <div className="space-y-2">
                {sm.fields.map(f => {
                  const meta = FIELD_META[f] || { label: f, step: 1 };
                  const raw = settings.escenarios?.[sm.key]?.[f];
                  const display = meta.pct ? (raw ?? 0) * 100 : (raw ?? 0);
                  const onChange = (v) => set(`escenarios.${sm.key}.${f}`, meta.pct ? v / 100 : v);
                  return (
                    <NumInput key={f} label={meta.label} value={display} step={meta.step} suffix={meta.unit}
                              onChange={onChange}
                              tid={`${sm.key}-${f}`}
                              inline />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

const Section = ({ title, help, warn, children }) => (
  <div className="card-base p-5">
    <div className="mb-3">
      <h3 className="font-semibold text-slate-900">{title}</h3>
      {help && <p className="text-xs text-slate-500 mt-1">{help}</p>}
      {warn && <p className="text-xs text-amber-700 mt-1">⚠ {warn}</p>}
    </div>
    {children}
  </div>
);

const NumInput = ({ label, value, step, suffix, onChange, tid, inline }) => (
  <div className={inline ? 'flex items-center justify-between gap-2' : ''}>
    <label className={`text-xs text-slate-600 ${inline ? 'flex-1' : 'block mb-1'}`}>{label}</label>
    <div className="relative" style={{ width: inline ? '7rem' : '100%' }}>
      <input
        type="number"
        step={step}
        className="input-base text-right text-sm py-1.5 pr-7"
        value={value ?? 0}
        onChange={e => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
        data-testid={`credito-set-${tid}`}
      />
      {suffix && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{suffix}</span>}
    </div>
  </div>
);

export default CreditoScoringSettingsSection;

// === Scoring Tax Overview ===
// Shows which tax (ITP/AJD/IVA), rate % and base source applies to each scenario
// of the credit scoring. Reads live from /settings/tax-rules so the admin
// sees the same values the engine will use.
const SCENARIO_TO_RULE = [
  { key: 'pacto',        label: 'Pacto / Quita',          rule: 'COMPRA_DIRECTA', ruleLabel: 'Adjudicación Inmueble', note: 'Usa la regla de Adjudicación Inmueble.' },
  { key: 'cobertura',    label: 'Subasta / Cobertura',    rule: 'CREDITO',        ruleLabel: 'Compra de Crédito',     note: 'Usa la regla de Compra de Crédito.' },
  { key: 'cdr',          label: 'Subasta / CDR',          rule: 'CREDITO',        ruleLabel: 'Compra de Crédito',     note: 'Usa la regla de Compra de Crédito.' },
  { key: 'adjudicacion', label: 'Subasta / Adjudicación', rule: 'MULTI',          ruleLabel: 'Compra de Crédito + Adjudicación Inmueble', note: 'Suma: Compra de Crédito + Adjudicación Inmueble.' },
];

const ScoringTaxOverview = () => {
  const [rules, setRules] = useState(null);
  const [labels, setLabels] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings/tax-rules`, { withCredentials: true })
      .then(r => { setRules(r.data.rules); setLabels(r.data.base_labels || {}); })
      .catch(() => {});
  }, []);

  if (!rules) return <p className="text-xs text-slate-400">Cargando reglas…</p>;

  const cellFor = (ruleKey) => {
    const r = rules[ruleKey] || {};
    return {
      impuesto: r.impuesto || '—',
      tipo: r.tipo_default != null ? `${r.tipo_default}%` : '—',
      base: labels[r.base_fuente] || r.base_fuente || '—',
    };
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="scoring-tax-overview">
        <thead>
          <tr className="text-xs text-slate-500 uppercase border-b border-slate-200">
            <th className="text-left py-2 px-3">Escenario</th>
            <th className="text-left py-2 px-3">Impuesto</th>
            <th className="text-right py-2 px-3">Tipo</th>
            <th className="text-left py-2 px-3">Base imponible</th>
          </tr>
        </thead>
        <tbody>
          {SCENARIO_TO_RULE.map(sc => {
            if (sc.rule === 'MULTI') {
              const credito = cellFor('CREDITO');
              const remate = cellFor('REMATE');
              return (
                <tr key={sc.key} className="border-b border-slate-100" data-testid={`scoring-tax-${sc.key}`}>
                  <td className="py-2.5 px-3 font-medium text-slate-800 align-top">
                    {sc.label}
                    <p className="text-[11px] text-slate-400 mt-1">{sc.note}</p>
                  </td>
                  <td className="py-2.5 px-3 align-top">
                    <div><span className="font-medium">{credito.impuesto}</span> <span className="text-xs text-slate-400">(Compra de Crédito)</span></div>
                    <div className="mt-1"><span className="font-medium">{remate.impuesto}</span> <span className="text-xs text-slate-400">(Adjudicación Inmueble)</span></div>
                  </td>
                  <td className="py-2.5 px-3 text-right align-top tabular-nums">
                    <div>{credito.tipo}</div>
                    <div className="mt-1">{remate.tipo}</div>
                  </td>
                  <td className="py-2.5 px-3 align-top text-xs text-slate-600">
                    <div>{credito.base}</div>
                    <div className="mt-1">{remate.base}</div>
                  </td>
                </tr>
              );
            }
            const c = cellFor(sc.rule);
            return (
              <tr key={sc.key} className="border-b border-slate-100" data-testid={`scoring-tax-${sc.key}`}>
                <td className="py-2.5 px-3 font-medium text-slate-800 align-top">
                  {sc.label}
                  <p className="text-[11px] text-slate-400 mt-1">{sc.note}</p>
                </td>
                <td className="py-2.5 px-3 align-top font-medium">{c.impuesto}</td>
                <td className="py-2.5 px-3 text-right align-top tabular-nums">{c.tipo}</td>
                <td className="py-2.5 px-3 align-top text-xs text-slate-600">{c.base}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 mt-2">
        Para cambiar estos impuestos, edita <strong>Parámetros › Impuestos</strong>. Los campos editables abajo (ITP/AJD %) sirven como override manual por escenario si los dejas distintos de 0; en caso contrario se usan las reglas globales.
      </p>
    </div>
  );
};
